import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import classNames from 'classnames';
import isFunction from 'lodash/isFunction';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { Spinner } from '@micromag/core/components';
import { useMediaThumbnail, useMediaCurrentTime, useMediaDuration, useMediaReady, useProgressSteps } from '@micromag/core/hooks';
import { getMediaFilesAsArray, getVideoSupportedMimes } from '@micromag/core/utils';

function useSources(media) {
  var _ref = media || {},
    _ref$files = _ref.files,
    mediaFiles = _ref$files === void 0 ? null : _ref$files,
    _ref$metadata = _ref.metadata,
    metadata = _ref$metadata === void 0 ? null : _ref$metadata;
  var _ref2 = metadata || {},
    _ref2$mime = _ref2.mime,
    mediaMime = _ref2$mime === void 0 ? null : _ref2$mime;
  var files = useMemo(function () {
    return getMediaFilesAsArray(mediaFiles);
  }, [mediaFiles]);
  var sources = useMemo(function () {
    if (files.length === 0) {
      return null;
    }
    var supportedMimes = getVideoSupportedMimes();
    if (supportedMimes.length === 0) {
      supportedMimes = ['video/mp4', 'video/webm'];
    }
    var supportedFiles = files.filter(function (file) {
      var fileHandle = file.handle || file.id;
      var _file$mime = file.mime,
        mime = _file$mime === void 0 ? "video/".concat(fileHandle === 'h264' ? 'mp4' : fileHandle) : _file$mime;
      return supportedMimes.indexOf(mime) !== -1;
    });
    var supportedFilesWithoutOriginal = supportedFiles.filter(function (file) {
      var fileHandle = file.handle || file.id;
      return fileHandle !== 'original';
    });
    return (supportedFilesWithoutOriginal.length > 0 ? supportedFilesWithoutOriginal : supportedFiles).sort(function (_ref3, _ref4) {
      var _ref3$size = _ref3.size,
        a = _ref3$size === void 0 ? Infinity : _ref3$size;
      var _ref4$size = _ref4.size,
        b = _ref4$size === void 0 ? Infinity : _ref4$size;
      if (a === b) {
        return 0;
      }
      return a > b ? 1 : -1;
    });
  }, [files]);

  // @NOTE: Media is an animated image and doesn't have source files in video formats
  var _ref5 = files.find(function (_ref6) {
      var handle = _ref6.handle;
      return handle === 'original';
    }) || {},
    _ref5$type = _ref5.type,
    originalType = _ref5$type === void 0 ? null : _ref5$type,
    _ref5$mime = _ref5.mime,
    originalMime = _ref5$mime === void 0 ? mediaMime : _ref5$mime;
  var originalFileIsImage = originalType === 'image' || originalMime !== null && originalMime.indexOf('image/') === 0;
  return {
    sources: sources !== null && sources.length > 0 ? sources : null,
    files: files,
    isImage: originalFileIsImage
  };
}

var styles = {"container":"micromag-element-video-container","withSize":"micromag-element-video-withSize","media":"micromag-element-video-media","spinner":"micromag-element-video-spinner"};

var propTypes = {
  media: PropTypes$1.videoMedia,
  thumbnail: PropTypes.oneOf([PropTypes.string, PropTypes$1.imageMedia]),
  width: PropTypes.number,
  height: PropTypes.number,
  mediaRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  muted: PropTypes.bool,
  autoPlay: PropTypes.bool,
  paused: PropTypes.bool,
  loop: PropTypes.bool,
  playsInline: PropTypes.bool,
  preload: PropTypes.oneOf(['auto', 'metadata', 'none', null]),
  disablePictureInPicture: PropTypes.bool,
  shouldLoad: PropTypes.bool,
  withoutCors: PropTypes.bool,
  className: PropTypes.string,
  innerClassName: PropTypes.string,
  onReady: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onEnded: PropTypes.func,
  onSeeked: PropTypes.func,
  onTimeUpdate: PropTypes.func,
  onProgressStep: PropTypes.func,
  onDurationChange: PropTypes.func,
  onVolumeChange: PropTypes.func,
  onSuspend: PropTypes.func,
  onSuspended: PropTypes.func,
  onPlayError: PropTypes.func,
  focusable: PropTypes.bool,
  withPoster: PropTypes.bool,
  withLoading: PropTypes.bool
};
var defaultProps = {
  media: null,
  thumbnail: null,
  width: null,
  height: null,
  mediaRef: null,
  muted: false,
  autoPlay: false,
  paused: false,
  loop: false,
  playsInline: true,
  preload: 'auto',
  disablePictureInPicture: true,
  shouldLoad: true,
  withoutCors: false,
  className: null,
  innerClassName: null,
  onReady: null,
  onPlay: null,
  onPause: null,
  onEnded: null,
  onSeeked: null,
  onTimeUpdate: null,
  onProgressStep: null,
  onDurationChange: null,
  onVolumeChange: null,
  onSuspend: null,
  onSuspended: null,
  onPlayError: null,
  focusable: true,
  withPoster: false,
  withLoading: false
};
var Video = function Video(_ref) {
  var media = _ref.media,
    thumbnail = _ref.thumbnail,
    width = _ref.width,
    height = _ref.height,
    mediaRef = _ref.mediaRef,
    muted = _ref.muted,
    autoPlay = _ref.autoPlay,
    paused = _ref.paused,
    loop = _ref.loop,
    playsInline = _ref.playsInline,
    preload = _ref.preload,
    shouldLoad = _ref.shouldLoad,
    withoutCors = _ref.withoutCors,
    className = _ref.className,
    innerClassName = _ref.innerClassName,
    onReady = _ref.onReady,
    customOnPlay = _ref.onPlay,
    onPause = _ref.onPause,
    onEnded = _ref.onEnded,
    onSeeked = _ref.onSeeked,
    onTimeUpdate = _ref.onTimeUpdate,
    onProgressStep = _ref.onProgressStep,
    customOnDurationChange = _ref.onDurationChange,
    customOnVolumeChange = _ref.onVolumeChange,
    customOnSuspend = _ref.onSuspend,
    onSuspended = _ref.onSuspended,
    onPlayError = _ref.onPlayError,
    focusable = _ref.focusable,
    withPoster = _ref.withPoster,
    withLoading = _ref.withLoading,
    disablePictureInPicture = _ref.disablePictureInPicture;
  var _ref2 = media || {},
    _ref2$url = _ref2.url,
    mediaUrl = _ref2$url === void 0 ? null : _ref2$url,
    _ref2$metadata = _ref2.metadata,
    metadata = _ref2$metadata === void 0 ? null : _ref2$metadata;
  var _ref3 = metadata || {},
    _ref3$description = _ref3.description,
    description = _ref3$description === void 0 ? null : _ref3$description,
    _ref3$has_audio = _ref3.has_audio,
    hasAudio = _ref3$has_audio === void 0 ? null : _ref3$has_audio;
  var finalThumbnail = useMediaThumbnail(media, thumbnail);
  var _useSources = useSources(media),
    sources = _useSources.sources,
    isImage = _useSources.isImage;
  var isImageWithoutSourceFile = isImage && (sources === null || sources.length === 0);
  var _ref6 = useRef(null);
  var currentTime = useMediaCurrentTime(_ref6.current, {
    id: mediaUrl,
    disabled: paused || onProgressStep === null
  });
  var duration = useMediaDuration(_ref6.current, {
    id: mediaUrl
  });
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showLoading = _useState2[0],
    setShowLoading = _useState2[1];
  var ready = useMediaReady(_ref6.current, {
    id: mediaUrl
  });
  useEffect(function () {
    var id = null;
    setShowLoading(false);
    if (mediaUrl && withLoading) {
      id = setTimeout(function () {
        setShowLoading(true);
      }, 2000);
    }
    return function () {
      clearTimeout(id);
    };
  }, [mediaUrl, withLoading]);
  var withSize = width !== null && height !== null;
  useEffect(function () {
    if (duration > 0 && customOnDurationChange !== null) {
      customOnDurationChange(duration);
    }
  }, [duration, customOnDurationChange]);
  var onVolumeChange = useCallback(function () {
    var _ref$current = _ref6.current,
      element = _ref$current === void 0 ? null : _ref$current;
    if (element === null) {
      return;
    }
    if (customOnVolumeChange !== null) {
      customOnVolumeChange(element.volume);
    }
  }, [customOnVolumeChange]);

  // Manage suspend
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isSuspended = _useState4[0],
    setIsSuspended = _useState4[1];
  var onPlay = useCallback(function (e) {
    if (isSuspended) {
      setIsSuspended(false);
    }
    if (customOnPlay !== null) {
      customOnPlay(e);
    }
  }, [isSuspended, setIsSuspended, customOnPlay]);
  var onPlaying = useCallback(function () {
    if (isSuspended) {
      setIsSuspended(false);
    }
  }, [isSuspended, setIsSuspended]);
  var onSuspend = useCallback(function (e) {
    if (e.currentTarget.paused && !paused && !isSuspended) {
      setIsSuspended(true);
      if (onSuspended !== null) {
        onSuspended();
      }
    }
    if (customOnSuspend !== null) {
      customOnSuspend(e);
    }
  }, [isSuspended, paused, setIsSuspended, customOnSuspend, onSuspended]);
  useEffect(function () {
    if (ready && onReady !== null) {
      onReady();
    }
  }, [ready, onReady]);
  useEffect(function () {
    var _ref$current2 = _ref6.current,
      element = _ref$current2 === void 0 ? null : _ref$current2;
    if (element === null) {
      return;
    }
    var isPaused = element.paused;
    if (paused && !isPaused) {
      element.pause();
    } else if (!paused && isPaused) {
      element.play()["catch"](function (e) {
        if (onPlayError !== null) {
          onPlayError(e);
        }
      });
    }
  }, [paused, media, onPlayError]); // test media here for fun

  useProgressSteps({
    currentTime: currentTime,
    duration: duration,
    disabled: paused,
    onStep: onProgressStep
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty({}, styles.withSize, withSize), className]),
    style: withSize ? {
      width: width,
      height: height
    } : null
  }, isImageWithoutSourceFile && shouldLoad ? /*#__PURE__*/React.createElement("img", {
    src: mediaUrl,
    alt: description,
    tabIndex: "-1",
    className: classNames([styles.media, innerClassName])
  }) : null, !isImageWithoutSourceFile ? /*#__PURE__*/React.createElement("video", {
    key: mediaUrl,
    ref: function ref(newRef) {
      _ref6.current = newRef;
      if (mediaRef !== null && isFunction(mediaRef)) {
        mediaRef(newRef);
      } else if (mediaRef !== null) {
        mediaRef.current = newRef;
      }
    },
    src: sources === null && shouldLoad ? "".concat(mediaUrl, "#t=0.001") : null,
    autoPlay: autoPlay && !paused,
    loop: loop,
    muted: muted,
    poster: shouldLoad && withPoster && finalThumbnail !== null ? finalThumbnail.url || null : null,
    preload: shouldLoad ? preload : 'none',
    playsInline: playsInline,
    crossOrigin: withoutCors ? 'anonymous' : null,
    disablePictureInPicture: disablePictureInPicture,
    tabIndex: focusable ? '0' : '-1',
    className: classNames([styles.media, innerClassName]),
    onPlay: onPlay,
    onPlaying: onPlaying,
    onPause: onPause,
    onEnded: onEnded,
    onSeeked: onSeeked,
    onVolumeChange: onVolumeChange,
    onTimeUpdate: onTimeUpdate,
    onSuspend: onSuspend,
    "data-has-audio": hasAudio,
    "data-is-suspended": isSuspended,
    "aria-hidden": true
  }, (shouldLoad && sources !== null ? sources : []).map(function (_ref5) {
    var sourceUrl = _ref5.url,
      sourceMime = _ref5.mime;
    return /*#__PURE__*/React.createElement("source", {
      key: "source-".concat(sourceUrl, "-").concat(sourceMime),
      src: sourceUrl !== null ? "".concat(sourceUrl, "#t=0.001") : null,
      type: sourceMime
    });
  })) : null, !isImageWithoutSourceFile && !ready && showLoading ? /*#__PURE__*/React.createElement(Spinner, {
    className: styles.spinner
  }) : null);
};
Video.propTypes = propTypes;
Video.defaultProps = defaultProps;
var Video$1 = /*#__PURE__*/React.forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Video, Object.assign({
    mediaRef: ref
  }, props));
});

export { Video$1 as default };
